import { useTracking } from 'react-tracking';
import LikeOutline from '@simplywallst/ui-core/icons/LikeOutline';
import CommentOutline from '@simplywallst/ui-core/icons/CommentOutline';
import UserProfileOutline from '@simplywallst/ui-core/icons/UserProfileOutline';
import { toCurrencyTruncated } from '@/utilities/formatting';
import { NarrativeAuthor } from '@/components/NarrativeAuthor';
import { Badge } from '@simplywallst/ui-core/next';

import {
  ROUTE_NARRATIVE_COMMUNITY,
  ROUTE_NARRATIVE_COMMUNITY_DETAIL,
} from '@/constants/routes';

import type { FragmentOf } from 'gql.tada';
import { readFragment } from 'gql.tada';
import { NARRATIVE_CARD_FRAGMENT } from '../../hooks/useNarrativeById/narrativeCardFragment';
import { Heading, cn } from '@simplywallst/ui-core/next';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import { getCompanyUrlSegments } from '@/pages/Narrative/utils/getNarrativeUrl';
import { NarrativeCardExcerpt } from './components/NarrativeCardExcerpt';
import { RouterLinkOptimised } from './../RouterLinkOptimised';
import type { HeadingElements } from '@/utilities/heading';
import { useUserProfile } from '@/hooks/useUserProfile';
import { ValuationMetrics } from '@/pages/Narrative/components/ValuationMetrics';
import { CompanyLogo } from './../../../CompanyLogo';
import { NarrativeSponsorshipBadge } from '@/pages/Narrative/components/NarrativeSponsorshipBadge';
import { NarrativeCardTimeBadge } from '@/components/NarrativeCard/components/NarrativeCardTimeBadge';

export type PopulatedStateBaseProps = {
  onClick?: () => void;
  className?: string;
  showCompanyIcon?: boolean;
  titleAs?: HeadingElements;
};

type Props = PopulatedStateBaseProps & {
  narrative: FragmentOf<typeof NARRATIVE_CARD_FRAGMENT>;
};

export const PopulatedState = ({
  narrative,
  onClick,
  className,
  showCompanyIcon,
  titleAs = 'h3',
}: Props) => {
  const { trackEvent } = useTracking();
  const {
    company,
    primaryCount,
    slug,
    updatedAt,
    owner,
    valuation,
    voteCount,
    isPublic,
    commentCount,
    type,
    isSponsored,
    draftUpdate,
    earliestPublishedUpdate,
    latestPublishedUpdate,
    excerpt,
    title,
  } = readFragment(NARRATIVE_CARD_FRAGMENT, narrative);

  const { analysisValue, data, name, info, tickerSymbol } = company ?? {};
  const { publicId: currentUserPublicId } = useUserProfile();
  const isOwner = currentUserPublicId === owner.publicId;
  const currencyIso = getTradingItemCurrencyISO(data?.currencyInfo);
  const isDraft = isOwner && !earliestPublishedUpdate?.willLockAt;

  const targetExcerpt =
    draftUpdate?.coreExcerpt || latestPublishedUpdate?.coreExcerpt || excerpt; // TODO post migration, remove excerpt
  const targetTitle =
    draftUpdate?.title || latestPublishedUpdate?.title || title;
  const targetIntrinsicDiscount =
    draftUpdate?.valuation?.intrinsicDiscount || valuation?.intrinsicDiscount;
  const targetFairValue =
    draftUpdate?.valuation?.fairValue || valuation?.fairValue;
  const targetRevenuePercentageChange =
    draftUpdate?.valuation?.revenuePercentageChange ||
    valuation?.revenuePercentageChange;

  const formattedFairValue = toCurrencyTruncated(
    targetFairValue || 0,
    2,
    currencyIso ?? 'XXX'
  );

  const titleNode = (
    <Heading
      as={titleAs}
      className={cn(
        'm-0 line-clamp-2 text-base font-strong text-solid',
        isPublic &&
          'group-focus-within/narrative-link:underline group-hover/narrative-link:underline'
      )}
    >
      {targetTitle || `I believe ${name}'s Fair Value is ${formattedFairValue}`}
    </Heading>
  );

  const { canonicalURL } = company ?? { canonicalURL: '' };

  const { country, industry, exchangeTicker, companyName } =
    getCompanyUrlSegments(canonicalURL);

  return (
    <div
      className={cn(
        'grid h-full grid-rows-[1fr_auto] overflow-hidden rounded-x1_5 bg-surface-2',
        className
      )}
    >
      <div
        className={cn(
          'relative grid bg-surface-3'
          // showCompanyIcon ? 'h-[230px]' : 'h-[189.5px]',
          // isPublic && 'hover:bg-p-grey-10 focus:bg-p-grey-10'
        )}
      >
        {showCompanyIcon && (
          <div
            className={cn(
              'grid min-h-x3 grid-cols-[1fr_max-content] items-center justify-start gap-x1 p-x2 pb-0',
              Boolean(isSponsored) &&
                'grid-cols-[minmax(0,1fr)_max-content_max-content]'
            )}
          >
            <RouterLinkOptimised
              className="focus-outline grid grid-cols-[auto] no-underline"
              to={{
                type: ROUTE_NARRATIVE_COMMUNITY,
                payload: {
                  country,
                  exchange_ticker: exchangeTicker,
                  industry,
                  companyname: companyName,
                  slug,
                },
              }}
              onClick={() =>
                trackEvent({
                  action: 'click',
                  modifier: 'company',
                  companyId: company?.id,
                  uniqueSymbol: company?.tickerSymbol,
                })
              }
            >
              <div className="grid grid-cols-[max-content_auto] items-center gap-x1">
                {tickerSymbol && (
                  <CompanyLogo
                    website={info?.url}
                    tickerSymbol={tickerSymbol}
                    size={20}
                    hasLogo={!!info?.hasLogo}
                  />
                )}
                <span className="truncate text-tiny text-soft">{name}</span>
              </div>
            </RouterLinkOptimised>
            {isSponsored && name && (
              <NarrativeSponsorshipBadge companyName={name} />
            )}
          </div>
        )}
        <div
          className={cn(
            'grid items-center gap-x1 p-x2 pb-0',
            Boolean(isDraft && !showCompanyIcon)
              ? 'grid-cols-[1fr_max-content_max-content]'
              : 'grid-cols-[1fr_max-content]'
          )}
        >
          <NarrativeAuthor
            owner={{
              displayName: owner.displayName,
              publicId: owner.publicId,
              imageUrl: owner.imageUrl,
              role: owner.role,
              disclaimerType: owner.disclaimerType,
            }}
            priceTargetAnalystCount={analysisValue?.priceTargetAnalystCount}
            isAiNarrative={type === 'AI'}
          />
          {isDraft && (
            <div className="grid justify-end">
              <Badge tone="warning">Draft</Badge>
            </div>
          )}
        </div>
        {isPublic ? (
          <RouterLinkOptimised
            className="group/narrative-link min-h-x13 p-x2 no-underline decoration-p-white"
            to={{
              type: ROUTE_NARRATIVE_COMMUNITY_DETAIL,
              payload: {
                country,
                exchange_ticker: exchangeTicker,
                industry,
                companyname: companyName,
                slug,
              },
            }}
            onClick={onClick}
          >
            {titleNode}
            <NarrativeCardExcerpt canView>{targetExcerpt}</NarrativeCardExcerpt>
          </RouterLinkOptimised>
        ) : (
          <div className="min-h-x13 p-x2">
            <div className="cursor-default">{titleNode}</div>
            <NarrativeCardExcerpt>{targetExcerpt}</NarrativeCardExcerpt>
          </div>
        )}
      </div>
      <div className="grid gap-4 p-4">
        <ValuationMetrics
          formattedFairValue={formattedFairValue}
          targetIntrinsicDiscount={targetIntrinsicDiscount}
          targetRevenuePercentageChange={targetRevenuePercentageChange}
        />
        <div className="grid grid-cols-[auto_1fr] justify-between gap-x2">
          <div className="grid grid-cols-[repeat(3,max-content)] justify-start gap-x2 fill-softer text-xs text-softer">
            <div className="grid grid-cols-[min-content_min-content] items-center gap-x0_5">
              <LikeOutline className="size-x2" />

              <div>
                {voteCount.upvotes}
                <span className="sr-only">users have liked this narrative</span>
              </div>
            </div>
            <div className="grid grid-cols-[min-content_min-content] items-center gap-x0_5">
              <CommentOutline className="size-x2" />

              <div>
                {commentCount}
                <span className="sr-only">
                  users have commented on this narrative
                </span>
              </div>
            </div>
            <div className="grid grid-cols-[min-content_min-content] items-center gap-x0_5">
              <UserProfileOutline className="size-x2" />

              <div>
                {primaryCount}
                <span className="sr-only">
                  users have followed this narrative
                </span>
              </div>
            </div>
          </div>
          <NarrativeCardTimeBadge
            narrativeWillLockAt={earliestPublishedUpdate?.willLockAt}
            updatedAt={draftUpdate?.updatedAt ?? updatedAt}
            updateWillLockAt={latestPublishedUpdate?.willLockAt}
            lastFeaturedAt={narrative.lastFeaturedAt}
          />
        </div>
      </div>
    </div>
  );
};
